import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { atom, useRecoilState, useSetRecoilState } from 'recoil';
import useSWR from 'swr';

import { ADMIN_ACCESS_TOKEN_KEY } from '../../../App';
import { Filters, Worklist, Pagination } from '@components/organisms';
import { DEFAULT_CURRENT_PAGE } from '@components/organisms/Table/Pagination';
import { Body, dialogContentsState } from '@components/templates';
import { DEFAULT_SEARCH_KEY } from '@components/organisms/Table/Filters';
import SearchButton from '@components/atoms/Inputs/Button/Search';
import LicenseUser from '@components/molecules/LicenseInfo';
import { PrimaryButton } from '@components/atoms';
import { submitDebounce } from '../../../utils/lib';
import { StyledCreateAccountButton, StyledFlexColumnsWrapper, StyledSearchBarWrapper } from './styles';

interface ProductUser {
  activated: boolean;
  createdDate: string;
  deleted: boolean;
  email: string;
  firstName: string;
  id: number;
  lastName: string;
  permissionCombination: string;
  position: string | null;
  toDate: string;
  userId: string;
}

export const userPageCurrentPageState = atom({
  key: 'UserPageState',
  default: {
    countPerPage: 10,
    currentPage: 1,
    totalCount: 1,
    'order.name': 'id',
    'order.sort': 'DESC',
  },
});

export const hasSessionState = atom({
  key: 'hasSessionState',
  default: false,
});

const UserPage = () => {
  const navigate = useNavigate();
  const [accessToken, setAccessToken] = useState('');
  const [licenseInfo, setLicenseInfo] = useState<any>(null);
  const [valueState, setValueState] = useState({
    searchKey: 'ID',
    searchKeyword: '',
    searchDateType: '',
    dateRange: { startDate: '', endDate: '' },
  });
  const [pageConditionsValue, setPageConditionsValue] = useRecoilState(userPageCurrentPageState);
  const setDialogContents = useSetRecoilState(dialogContentsState);

  useEffect(() => {
    const newToken = localStorage.getItem(ADMIN_ACCESS_TOKEN_KEY);
    if (newToken && accessToken !== newToken) {
      setAccessToken(newToken);
    }
  }, []);

  const url = `api/user?countPerPage=${pageConditionsValue.countPerPage}&currentPage=${
    pageConditionsValue.currentPage
  }&searchType=${valueState.searchKey || ''}&searchValue=${valueState.searchKeyword || ''}&fromDate=${
    valueState.dateRange.startDate || ''
  }&searchDateType=${valueState.searchDateType || ''}&toDate=${valueState.dateRange.endDate || ''}`;

  const { data, error } = useSWR(!!accessToken.length ? [{ url, accessToken }] : null);

  useEffect(() => {
    if (valueState?.searchKeyword)
      setPageConditionsValue((prev) => ({
        ...prev,
        currentPage: DEFAULT_CURRENT_PAGE,
      }));
  }, [valueState]);

  if (error) {
    return <div>failed to load</div>;
  }

  if (!data) {
    return <div>loading...</div>;
  }

  if (!data.result) {
    if (data.code === 'UNAUTHORIZED') return <Navigate to={'/'} replace={true} />;
  }

  let productUsers: any[] = [];
  if (data) {
    if (data.code === 'UNAUTHORIZED') return <Navigate to={'/'} replace={true} />;

    if (data.code === '200') {
      const { countPerPage, currentPage, totalCount } = data.result.condition;
      const startNo = totalCount - (currentPage - 1) * countPerPage;
      data.result.results.forEach((item: ProductUser, index: number) =>
        productUsers.push({
          no: startNo - index,
          id: item.id,
          create_date: item.createdDate,
          userId: item.userId,
          name: `${item.lastName} ${item.firstName}`,
          email_address: item.email,
          account: item.deleted ? 'deleted' : item.activated ? 'activated' : 'deactivated',
        }),
      );
    }
  }

  const createAccount = () => {
    // 계정 생성 카운트 초과시 얼럿
    if (!licenseInfo.isUnlimitedUser && licenseInfo.activatedUserCount >= licenseInfo.limitUser) {
      setDialogContents({
        type: 'plain',
        body: `The Number of user accounts is full. \nTo create a new account, deactivate some accounts.`,
        footer: (
          <PrimaryButton
            onClick={async () => {
              setDialogContents({ type: 'hidden' });
            }}
          >
            OK
          </PrimaryButton>
        ),
      });
    } else {
      window.open(
        '/popup/create-account',
        '_blank',
        'toolbar=no,location=no,directories=no,titlebar=no,status=no,menubar=no,scrollbars=no,resizable=no,copyhistory=no,width=650,height=700',
      );
    }
  };

  const moveToDetailItemPage = (item: any) => {
    navigate(`/permissions/detail/${item.id}`);
  };

  /**
   * 이메일 마스킹 처리
   * 3자 미만일 경우 그냥 노출
   * 3자 이상일 경우 앞 두자리만 노출
   * @param email
   */
  const getMaskedEmail = (email: string) => {
    if (!email) {
      return '';
    }
    try {
      const cutEmailString = email.split('@');
      // 이메일 3자 이상
      if (cutEmailString[0].length > 2) {
        const emailPrefix = cutEmailString[0].substring(0, 2);
        const mask = Array.from({
          length: cutEmailString[0].length - 1,
        }).reduce((v) => `${v || ''}*`);
        const domain = cutEmailString[1];
        return `${emailPrefix}${mask}@${domain}`;
      } else {
        // 그외
        return email;
      }
    } catch {
      return '';
    }
  };

  return (
    <>
      <Body data-page-type={'table-only'}>
        <LicenseUser setLicenseInfo={setLicenseInfo} />
        <StyledFlexColumnsWrapper>
          <StyledSearchBarWrapper>
            <Filters
              searchConditions={valueState}
              setSearchConditions={setValueState}
              conditionType={{
                searchType: [
                  ...DEFAULT_SEARCH_KEY,
                  {
                    label: 'Email',
                    value: 'EMAIL',
                  },
                ],
              }}
            />
            <StyledCreateAccountButton onClick={submitDebounce(createAccount)}>
              Create Account
            </StyledCreateAccountButton>
          </StyledSearchBarWrapper>
          <Worklist
            headers={[
              { label: 'No', data_label: 'no' },
              { label: 'Create Date', data_label: 'create_date' },
              { label: 'User ID', data_label: 'userId' },
              { label: 'Name', data_label: 'name' },
              { label: 'Email address', data_label: 'email_address' },
              { label: 'Account', data_label: 'account' },
            ]}
            highlightColumn={'account'}
            data={{ dataArray: productUsers }}
            // onClickWorkListItem={(item: any) => moveToDetailItemPage(item)}
            customColumn={{
              userId: (row) => (
                <u
                  className={row.account === 'deleted' ? 'deleted' : ''}
                  onClick={() => {
                    if (row.account !== 'deleted') moveToDetailItemPage(row);
                  }}
                >
                  {row.userId}
                  {row.account !== 'deleted' && <SearchButton diameter={16} />}
                </u>
              ),
              email_address: (row) => <>{row.email_address}</>,
            }}
          />
          <Pagination conditions={data.result.condition} setConditions={setPageConditionsValue} />
        </StyledFlexColumnsWrapper>
      </Body>
    </>
  );
};

export default UserPage;
